import { useTu } from "@src/appV2/i18n/useTu";
import {
  formatISODate,
  formatTime,
  setDateValues,
  setHours,
  startOfDay,
} from "@clipboard-health/date-time";
import { Box, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { useGetShiftTimes } from "@src/appV2/redesign/DailyView/api/useGetShiftTimes";
import { TimeSlotAvatar } from "@src/appV2/redesign/Shift/TimeSlotAvatar";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { addMinutes, parseISO } from "date-fns";

import { Text } from "../../../components/Text";
const TIME_SLOTS: TimeSlot[] = ["am", "pm", "noc"];

const TIME_SLOT_SHORT_LABELS: Record<TimeSlot, string> = {
  am: "AM",
  pm: "PM",
  noc: "NOC",
};

interface ShiftDateAndSlotPickerProps {
  selectedDate: Date;
  selectedTimeSlot: TimeSlot;
  workplaceId: string;
  qualification: string;
  timezone: string;
  onDateChange: (date: Date) => void;
  onTimeSlotChange: (slot: TimeSlot) => void;
}

/**
 * Lets the user pick a date and a time slot (AM/PM/NOC) for a shift invite.
 * Displays the configured shift times for each slot so the user knows the hours.
 */
export function ShiftDateAndSlotPicker(props: ShiftDateAndSlotPickerProps) {
  const { tu } = useTu("chat");
  const timeSlotLabels: Record<TimeSlot, string> = {
    am: tu("AM Shift"),
    pm: tu("PM Shift"),
    noc: tu("NOC Shift"),
  };

  const {
    selectedDate,
    selectedTimeSlot,
    workplaceId,
    qualification,
    timezone,
    onDateChange,
    onTimeSlotChange,
  } = props;

  const isMobile = useIsMobile();
  const { data: shiftTimes } = useGetShiftTimes({ workplaceId, qualification });

  const minDate = parseISO(
    formatISODate(getServerNow(), timezone ? { timeZone: timezone } : undefined)
  );

  return (
    <Stack spacing={4}>
      <Box>
        <Text
          semibold
          sx={(theme) => ({ color: theme.palette.base?.base, marginBottom: 2 })}
          variant="body2"
        >
          {tu("Date")}
        </Text>
        <DatePicker
          minDate={minDate}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
            },
            // Render the calendar popover inside the dialog's DOM subtree so
            // chat-drawer click-outside detection doesn't treat picker clicks
            // as outside-clicks.
            popper: { disablePortal: true },
          }}
          value={selectedDate}
          onChange={(date) => {
            if (date) {
              onDateChange(date);
            }
          }}
        />
      </Box>
      <Box>
        <Text
          semibold
          sx={(theme) => ({ color: theme.palette.base?.base, marginBottom: 2 })}
          variant="body2"
        >
          {tu("Shift")}
        </Text>
        <Stack direction={isMobile ? "row" : "column"} spacing={2}>
          {TIME_SLOTS.map((slot) => {
            const isSelected = slot === selectedTimeSlot;
            const slotTimes = shiftTimes?.[slot];

            return (
              <Box
                key={slot}
                aria-checked={isSelected}
                aria-label={timeSlotLabels[slot]}
                role="radio"
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? 2 : 3,
                  padding: isMobile ? 2 : 3,
                  borderRadius: theme.borderRadius?.medium,
                  border: `1px solid ${isSelected ? theme.palette.base?.primary : theme.palette.border?.base}`,
                  backgroundColor: isSelected ? `${theme.palette.base?.primary}08` : "transparent",
                  cursor: "pointer",
                  flex: isMobile ? 1 : undefined,
                  flexDirection: isMobile ? "column" : "row",
                  "&:hover": {
                    borderColor: theme.palette.base?.primary,
                  },
                })}
                tabIndex={0}
                onClick={() => {
                  onTimeSlotChange(slot);
                }}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onTimeSlotChange(slot);
                  }
                }}
              >
                <TimeSlotAvatar timeSlot={slot} />
                <Stack
                  spacing={0}
                  sx={{
                    flex: isMobile ? undefined : 1,
                    alignItems: isMobile ? "center" : undefined,
                  }}
                >
                  <Text
                    semibold
                    sx={(theme) => ({
                      color: theme.palette.base?.base,
                      fontSize: isMobile ? theme.typography.caption.fontSize : undefined,
                    })}
                    variant={isMobile ? "caption" : "body2"}
                  >
                    {isMobile ? TIME_SLOT_SHORT_LABELS[slot] : timeSlotLabels[slot]}
                  </Text>
                  {slotTimes && !isMobile && (
                    <Text sx={(theme) => ({ color: theme.palette.base?.muted })} variant="caption">
                      {formatShiftTimeRange(slotTimes, timezone)}
                    </Text>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}

/**
 * Formats shift time definitions as a human-readable range (e.g. "7:00 AM - 3:00 PM").
 *
 * Interprets the API's hour/minute values as workplace-local time using
 * @clipboard-health/date-time's timezone-aware helpers, so the displayed range
 * reflects the workplace's clock regardless of the device timezone.
 */
function formatShiftTimeRange(
  slotTimes: {
    start: { hour: number; minute: number };
    duration: { hours: number; minutes: number };
  },
  timezone: string
): string {
  const options = timezone ? { timeZone: timezone } : undefined;

  const start = timezone
    ? addMinutes(
        setHours(startOfDay(getServerNow(), { timeZone: timezone }), slotTimes.start.hour, {
          timeZone: timezone,
        }),
        slotTimes.start.minute
      )
    : setDateValues(getServerNow(), {
        hours: slotTimes.start.hour,
        minutes: slotTimes.start.minute,
        seconds: 0,
        milliseconds: 0,
      });
  const durationMinutes = slotTimes.duration.hours * 60 + slotTimes.duration.minutes;
  const end = addMinutes(start, durationMinutes);

  return `${formatTime(start, options)} - ${formatTime(end, options)}`;
}
